import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Image,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import type { DependencyState } from '@isg/shared-types';
import { fetchHealth } from '../api/health';
import { probeMeWithoutToken } from '../api/me';
import { ANLAM, BANT_LOGO_YUKSEKLIK_PX, MARKA } from '../theme';

const DEPENDENCY_LABEL: Record<DependencyState, string> = {
  up: 'bağlı',
  down: 'erişilemiyor',
  not_configured: 'konfigüre değil',
};

/**
 * Giriş ekranı (F1·S10) — F0·S1 iskele doğrulaması + F0·S3 SSO girişinin
 * gerçek kabuğa bağlanmış hali. Sağlık paneli kaldı (smoke e2e'nin çift
 * yönlü göstergesi, §9): API ayaktaysa zincir yeşil, değilse KIRMIZI yanar.
 */
export function LoginScreen({ onLogin }: { onLogin: () => void }): React.JSX.Element {
  const health = useQuery({ queryKey: ['health'], queryFn: ({ signal }) => fetchHealth(signal) });
  // Negatif kanıt (§9): korumalı uca token'sız istek — beklenen 401.
  const [anonStatus, setAnonStatus] = useState<number | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    probeMeWithoutToken(controller.signal)
      .then(setAnonStatus)
      .catch(() => setAnonStatus(null));
    return () => controller.abort();
  }, []);

  // ✏️ DEMO GİRİŞ PAKETİ (ürün sahibi 25.09.2026): bu ekran demo yüzeyi
  // DEĞİLDİR — demo kipinde de üretimle aynı akış (tek düğme → Keycloak formu).
  // Persona kartları ve şerit kalktı; uyarı Keycloak formunun başlığında, kimlik
  // bilgileri e-postayla (shell/DemoSeridi.tsx dosya başı). Bu dosya
  // `shell/DemoSeridi`yi import ETMEZ (demo-seridi.spec kaynak envanteri).
  return (
    <>
      <Center mih="100vh" p="md">
        <Card withBorder maw={480} w="100%" padding="xl">
          <Stack>
            <div>
              {/* ✏️ 01.10.2026 — GÖRÜNEN YÜZ (SafeOps): kart zemini temayla değişir,
                  logo mürekkebi de onunla değişir — iki dosya, tek görünür (Mantine
                  `darkHidden`/`lightHidden`: renk şemasına göre DOM'da durur, çizilmez).
                  Yükseklik bant logosuyla aynı ölçek (BANT_LOGO_YUKSEKLIK_PX). */}
              <Image
                src={MARKA.logoAcikZemin}
                alt={MARKA.urunAdi}
                h={BANT_LOGO_YUKSEKLIK_PX}
                w="auto"
                fit="contain"
                mb="sm"
                darkHidden
                data-testid="giris-logosu"
              />
              <Image
                src={MARKA.logo}
                alt={MARKA.urunAdi}
                h={BANT_LOGO_YUKSEKLIK_PX}
                w="auto"
                fit="contain"
                mb="sm"
                lightHidden
                data-testid="giris-logosu-koyu"
              />
              <Text size="xs" tt="uppercase" c="dimmed" fw={600}>
                Faz 1.5 · Salt-okunur görüntüleme
              </Text>
              <Title order={2}>{MARKA.urunAdi}</Title>
            </div>

            <Button data-testid="sso-login" onClick={onLogin} size="md">
              Keycloak ile giriş yap
            </Button>

            {health.isError && (
              <Alert color={ANLAM.engel} role="alert" title="API'ye ulaşılamadı">
                {health.error instanceof Error ? health.error.message : 'Bilinmeyen hata'} —{' '}
                <code>pnpm dev</code> ile API'nin 3000 portunda ayakta olduğunu doğrulayın.
              </Alert>
            )}
            {health.data && (
              <Stack gap="xs">
                <Text size="sm" c="teal.8">
                  Zincir çalışıyor — yanıt <code>@isg/validation</code> şemasından geçti (
                  {health.data.service} v{health.data.version}, {health.data.environment}).
                </Text>
                <Group gap="xs">
                  {Object.entries(health.data.dependencies).map(([name, durum]) => (
                    <Badge
                      key={name}
                      variant="light"
                      color={
                        durum === 'up'
                          ? ANLAM.olumlu
                          : durum === 'down'
                            ? ANLAM.engel
                            : ANLAM.dikkat
                      }
                    >
                      {name}: {DEPENDENCY_LABEL[durum as DependencyState]}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            )}

            <Text size="xs" c="dimmed" data-testid="anon-probe">
              Token'sız <code>/api/me</code>:{' '}
              {anonStatus === null ? (
                'ölçülemedi'
              ) : (
                <strong data-testid="anon-status">{anonStatus}</strong>
              )}{' '}
              (beklenen 401 — kapı gerçekten kapalı)
            </Text>
          </Stack>
        </Card>
      </Center>
    </>
  );
}
